import _ from 'lodash';
import { authHeaders, convertToFormData, del, get, post, put } from "../../utils/fetch";
import { DevEnv, HideFilterLevel, Hosts, RequestState } from "../../constants";
import { formatMoney } from '../../utils/number';

// ===========================
// HELPERS
// ===========================

export const getRagChatbotIframeUrl = (chatbot, isStandalone = false) => {
  if (isStandalone) {
    return `${Hosts.INTEGRATE_URL}/chatbot/${chatbot.id}/standalone`
  } else {
    return `${Hosts.INTEGRATE_URL}/chatbot/${chatbot.id}`
  }
}

// ===========================
// ACTIONS
// ===========================
export const FETCH_STANDARD_TEMPLATES = 'FETCH_STANDARD_TEMPLATES';
export const FETCH_VMS = 'FETCH_VMS';
export const FETCH_COMMUNITY_VMS = 'FETCH_COMMUNITY_VMS';
export const FETCH_COMMUNITY_VM_SPECS = 'FETCH_COMMUNITY_VM_SPECS';
export const CREATE_DEPLOYMENT = 'CREATE_DEPLOYMENT';
export const UPDATE_DEPLOYMENT = 'UPDATE_DEPLOYMENT';
export const CREATE_CUSTOM_TEMPLATE = 'CREATE_CUSTOM_TEMPLATE';
export const FETCH_CUSTOM_TEMPLATES = 'FETCH_CUSTOM_TEMPLATES';
export const FETCH_DEPLOYMENT_TEMPLATE = 'FETCH_DEPLOYMENT_TEMPLATE';
export const UPDATE_CUSTOM_TEMPLATE = 'UPDATE_CUSTOM_TEMPLATE';
export const DELETE_CUSTOM_TEMPLATE = 'DELETE_CUSTOM_TEMPLATE';
export const FETCH_JUPYTER_NOTEBOOKS = 'FETCH_JUPYTER_NOTEBOOKS';
export const FETCH_DEPLOYMENTS = 'FETCH_DEPLOYMENTS';
export const FETCH_TEMPLATES_WITH_FILTER = 'FETCH_TEMPLATES_WITH_FILTER';
export const FETCH_ON_DEMAND_MODELS_WITH_FILTER = 'FETCH_ON_DEMAND_MODELS_WITH_FILTER';
export const DELETE_DEPLOYMENT = 'DELETE_DEPLOYMENT';
export const START_DEPLOYMENT = 'START_DEPLOYMENT';
export const STOP_DEPLOYMENT = 'STOP_DEPLOYMENT';
export const FETCH_DEPLOYMENT_DETAIL = 'FETCH_DEPLOYMENT_DETAIL';
export const FETCH_DEPLOYMENT_BASE_DETAIL = 'FETCH_DEPLOYMENT_BASE_DETAIL';
export const UPDATE_DEPLOYMENT_BASE_DETAIL = 'UPDATE_DEPLOYMENT_BASE_DETAIL';
export const FETCH_DEPLOYMENT_LOGS = 'FETCH_DEPLOYMENT_LOGS';
export const FETCH_DEPLOYMENT_EVENTS = 'FETCH_DEPLOYMENT_EVENTS';
export const FETCH_PROTOTYPING_TEMPLATES = 'FETCH_PROTOTYPING_TEMPLATES';
export const FETCH_GPU_NODES = 'FETCH_GPU_NODES';
export const FETCH_TRAINING_TEMPLATES = 'FETCH_TRAINING_TEMPLATES';
export const FETCH_RAG_CHATBOTS = 'FETCH_RAG_CHATBOTS';
export const CREATE_RAG_CHATBOT = 'CREATE_RAG_CHATBOT';
export const FETCH_RAG_CHATBOT = 'FETCH_RAG_CHATBOT';
export const DELETE_RAG_CHATBOT = 'DELETE_RAG_CHATBOT';
export const UPDATE_RAG_CHATBOT = 'UPDATE_RAG_CHATBOT';
export const FETCH_LLM_ENDPOINTS = 'FETCH_LLM_ENDPOINTS';
export const FETCH_REGIONS_FROM_INSTANCES = 'FETCH_REGIONS_FROM_INSTANCES';
export const FETCH_INSTANCES_FROM_REGION = 'FETCH_INSTANCES_FROM_REGION';
export const FETCH_RAG_CHATBOT_ACCESS_TOKEN = 'FETCH_RAG_CHATBOT_ACCESS_TOKEN';
export const ROTATE_RAG_CHATBOT_ACCESS_TOKEN = 'ROTATE_RAG_CHATBOT_ACCESS_TOKEN';
export const CREATE_LLM_ENDPOINT = 'CREATE_LLM_ENDPOINT';
export const DELETE_LLM_ENDPOINT = 'DELETE_LLM_ENDPOINT';
export const UPDATE_LLM_ENDPOINT = 'UPDATE_LLM_ENDPOINT';
export const CREATE_CHATBOT_DOCUMENT = 'CREATE_CHATBOT_DOCUMENT';
export const FETCH_CHATBOT_DOCUMENTS = 'FETCH_CHATBOT_DOCUMENTS';
export const DELETE_CHATBOT_DOCUMENT = 'DELETE_CHATBOT_DOCUMENT';
export const UPDATE_CHATBOT_DOCUMENT = 'UPDATE_CHATBOT_DOCUMENT';
export const FETCH_RAG_CHATBOT_PRICE = 'FETCH_RAG_CHATBOT_PRICE';
export const FETCH_CHATBOT_DOCUMENT = 'FETCH_CHATBOT_DOCUMENT';
export const DUPLICATE_RAG_CHATBOT = 'DUPLICATE_RAG_CHATBOT';
export const FETCH_MODEL_APIS = 'FETCH_MODEL_APIS';
export const FETCH_MODEL_API_BY_ID = 'FETCH_MODEL_API_BY_ID';
export const FETCH_TEMPLATE_TAGS = 'FETCH_TEMPLATE_TAGS';
export const FETCH_ON_DEMAND_TAGS = 'FETCH_ON_DEMAND_TAGS';
export const FETCH_ALL_QUOTAS = 'FETCH_ALL_QUOTAS';
export const FETCH_CHATBOT_CHART_DATA = 'FETCH_CHATBOT_CHART_DATA';
export const FETCH_CHATBOT_PROMPTS = 'FETCH_CHATBOT_PROMPTS';
export const FETCH_GPU_CLUSTERS = 'FETCH_GPU_CLUSTERS';
export const FETCH_GPU_CLUSTER_DETAIL = 'FETCH_GPU_CLUSTER_DETAIL';
export const FETCH_GPU_CLUSTER_BASE_DETAIL = 'FETCH_GPU_CLUSTER_BASE_DETAIL';
export const FETCH_GPU_CLUSTER_LOGS = 'FETCH_GPU_CLUSTER_LOGS';
export const SCALE_GPU_CLUSTER = 'SCALE_GPU_CLUSTER';
export const FETCH_IMAGES_BY_VMS = 'FETCH_IMAGES_BY_VMS';
export const SET_READ_EVENT = 'SET_READ_EVENT';
// export const FETCH_USER_ORGANIZATIONS = 'FETCH_USER_ORGANIZATIONS';
// export const CREATE_ORGANIZATION = 'CREATE_ORGANIZATION';

// ===========================
// SELECTORS
// ===========================

export const selectAllstandardTemplates = (state) => state?.models.standardTemplates;
export const selectNotebookTemplate = (state) => state?.models.prototypingTemplates[0];
export const selectTrainingTemplate = (state) => state?.models.trainingTemplates[0];
export const selectAllVMs = (state) => state?.models.vms;
export const selectAllNeuronVms = (state) => state?.models.neuronVms;
export const selectCommunityVMs = (state) => state?.models.communityVms;
export const selectCommunityVMSpecs = (state) => state?.models.communityVmSpecs;
export const selectAllLlmEndpoints = (state) => state?.models.llmEndpints;
export const selectAllCustomTemplates = (state, projectId) => state?.models.customTemplates[projectId] || [];
export const selectAllNotebooks = (state, projectId) => state?.models.jupyterNotebooks[projectId] || [];
export const selectAllDeployments = (state, projectId) => state?.models.deployments[projectId] || [];
export const selectAllGpuNodes = (state, projectId) => state?.models.gpuNodes[projectId] || [];
export const selectAllGpuClusters = (state, projectId) => state?.models.gpuClusters[projectId] || [];
export const selectAllRagChatbots = (state, projectId) => state?.models.ragChatbots[projectId] || [];
export const selectRagChatbot = (state, projectId, id) => {
  const chatbot = _.get(state, `models.ragChatbots.${projectId}.${id}`);
  if (chatbot) {
    return {
      ...chatbot
    };
  }
  return null;
};
export const selectDeployment = (state, projectId, suffix) => {
  const deployment = _.get(state, `models.deployments.${projectId}.${suffix}`);
  if (deployment) {
    return {
      ...deployment
    };
  }
  return null;
};
export const selectGpuCluster = (state, projectId, suffix) => {
  const cluster = _.get(state, `models.gpuClusters.${projectId}.${suffix}`);
  if (cluster) {
    return {
      ...cluster
    };
  }
  return null;
};
export const selectOndemandModels = (state, projectId) => state?.models.ondemandModels;
export const selectTotalApiPage = (state) => state?.models.totalOndemandModelPages;
export const selectTotalTempPage = (state) => state?.models.totalStandardTemplatesPages;
export const selectTotalCustomTempPage = (state) => state?.models.totalCustomTemplatesPages;
export const selectStanndardTempTags = (state) => state?.models.standardTemplateTags;
export const selectOndemandTags = (state) => state?.models.onDemandTags;
// export const selectCurrentOrg = (state) => selectOrganization(state, selectCurrentOrgId(state));
// export const selectOrganization = (state, organizationId) => {
//   return state.models.organizations[organizationId];
// };

// export const selectOrganizations = (state) => _.values(state.models.organizations);
export const selectRegionsFromInstances = (state) => state?.models.regionsFromInstances;
// export const selectRegionsFromCpuInstances = (state) => state?.models.regionsFromCpuInstances;
export const selectInstancesFromRegion = (state) => state?.models.instancesFromRegion;
export const selectAllQuotas = (state) => state?.models.quotas;

export const selectDeploymentTemplate = state => state?.models.deploymentTemplate;

// ===========================
// MODEL
// ===========================
const Ai = {
  actions: {
    fetchstandardTemplates: (page = 1, number = 9) => async (dispatch, getState) => {
      let result = await get({
        host: Hosts.EDGE_CLOUD_CONTROLLER_API,
        url: `/deployment_template/list_standard_templates`,
        params: { page: page - 1, number, category: 'serving', hidden: false },
        headers: authHeaders(),
        action: FETCH_STANDARD_TEMPLATES,
        dispatch,
      });
      return selectAllstandardTemplates(getState());
    },
    fetchPrototypingTemplates: (page = 1, number = 10) => async (dispatch, getState) => {
      let result = await get({
        host: Hosts.EDGE_CLOUD_CONTROLLER_API,
        url: `/deployment_template/list_standard_templates`,
        params: { page: page - 1, number, category: 'prototyping' },
        headers: authHeaders(),
        action: FETCH_PROTOTYPING_TEMPLATES,
        dispatch,
      });
      return selectAllstandardTemplates(getState());
    },
    fetchTrainingTemplates: (page = 1, number = 10) => async (dispatch, getState) => {
      let result = await get({
        host: Hosts.EDGE_CLOUD_CONTROLLER_API,
        url: `/deployment_template/list_standard_templates`,
        params: { page: page - 1, number, category: 'training' },
        headers: authHeaders(),
        action: FETCH_TRAINING_TEMPLATES,
        dispatch,
      });
      return selectAllstandardTemplates(getState());
    },
    fetchVMs: (isFullList = false) => async (dispatch, getState) => {
      let result = await get({
        host: Hosts.EDGE_CLOUD_API,
        url: `/resource/vm/list?expand=resource_ids`,
        params: {},
        headers: authHeaders(),
        action: FETCH_VMS,
        data: { isFullList },
        dispatch,
      });
      return selectAllVMs(getState());
    },
    fetchCommunityVMs: (projectId, filter) => async (dispatch, getState) => {
      const params = { projectId, env: (DevEnv === 'staging' ? 'prod' : DevEnv) };
      if (filter) {
        if (filter.mem) {
          params.memoryLowThreshold = filter.mem;
        }
        if (filter.disk) {
          params.ephemeralStorageLowThreshold = filter.disk;
        }
        if (filter.gpu) {
          params.gpu = filter.gpu;
        }
        if (filter.gpuCount) {
          params.gpuLowThreshold = filter.gpuCount;
        }
        if (filter.price) {
          params.priceLowThreshold = filter.price;
        }
        if (filter.vRam) {
          params.vramLowThreshold = filter.vRam;
        }
      }
      let result = await get({
        host: Hosts.EDGE_CLOUD_CONTROLLER_API,
        url: `/community/nodes`,
        params: params,
        headers: authHeaders(),
        action: FETCH_COMMUNITY_VMS,
        dispatch,
      });
      return selectCommunityVMs(getState());
    },
    fetchCommunityFilterVMs: (projectId) => async (dispatch, getState) => {
      let result = await get({
        host: Hosts.EDGE_CLOUD_CONTROLLER_API,
        url: `/community/gpu`,
        params: { projectId, env: (DevEnv === 'staging' ? 'prod' : DevEnv) },
        headers: authHeaders(),
        action: FETCH_COMMUNITY_VM_SPECS,
        dispatch,
      });
      return _.get(result, 'body');
    },
    fetchCommunityVMSpecs: (projectId, nodeIds) => async (dispatch, getState) => {
      let result = await get({
        host: Hosts.EDGE_CLOUD_CONTROLLER_API,
        url: `/community/node-spec`,
        params: { projectId, nodes: nodeIds },
        headers: authHeaders(),
        action: FETCH_COMMUNITY_VM_SPECS,
        dispatch,
      });
      return selectCommunityVMSpecs(getState());
    },
    fetchImagesByVMs: (templateId, params) => async (dispatch, getState) => {
      let result = await get({
        host: Hosts.EDGE_CLOUD_CONTROLLER_API,
        url: `/container_image/compatible/${templateId}`,
        params: params,
        headers: authHeaders(),
        action: FETCH_IMAGES_BY_VMS,
        dispatch,
      });
      return _.get(result, 'body');
    },
    createDeployment: (body) => async (dispatch, getState) => {
      let result = await post({
        host: Hosts.EDGE_CLOUD_CONTROLLER_API,
        url: "/deployment",
        headers: authHeaders(),
        action: CREATE_DEPLOYMENT,
        dispatch,
        body: body
      });
    },
    updateDeployment: (shard, suffix, body) => async (dispatch, getState) => {
      console.log('updateDeployment body:', body)
      let result = await put({
        host: Hosts.EDGE_CLOUD_CONTROLLER_API,
        url: `/deployment/${shard}/${suffix}`,
        headers: authHeaders(),
        action: UPDATE_DEPLOYMENT,
        dispatch,
        body: body
      });
    },
    updateDeploymentBaseDetail: (baseId, body) => async (dispatch, getState) => {
      let result = await put({
        host: Hosts.EDGE_CLOUD_CONTROLLER_API,
        url: `/deployment/base/${baseId}`,
        headers: authHeaders(),
        action: UPDATE_DEPLOYMENT_BASE_DETAIL,
        dispatch,
        body: body
      });
      return _.get(result, 'body');
    },
    fetchCustomTemplates: (project_id, page = 1, number = 9) => async (dispatch, getState) => {
      let result = await get({
        host: Hosts.EDGE_CLOUD_CONTROLLER_API,
        url: `/deployment_template/list_custom_templates`,
        params: { project_id, page: page - 1, number },
        headers: authHeaders(),
        action: FETCH_CUSTOM_TEMPLATES,
        dispatch,
      });
      return selectAllCustomTemplates(getState());
    },
    fetchDeploymentTemplate: (templateId) => async (dispatch, getState) => {
      let result = await get({
        host: Hosts.EDGE_CLOUD_CONTROLLER_API,
        url: `/deployment_template/${templateId}`,
        headers: authHeaders(),
        action: FETCH_DEPLOYMENT_TEMPLATE,
        dispatch
      })
      return _.get(result, 'body')
    },
    createCustomTemplate: (body) => async (dispatch, getState) => {
      let result = await post({
        host: Hosts.EDGE_CLOUD_CONTROLLER_API,
        url: "/deployment_template",
        headers: authHeaders(),
        action: CREATE_CUSTOM_TEMPLATE,
        dispatch,
        body: body
      });
      // console.log('createCustomTemplate result:', result)
      return _.get(result, 'body');
    },
    updateCustomTemplate: (id, body) => async (dispatch, getState) => {
      let result = await put({
        host: Hosts.EDGE_CLOUD_CONTROLLER_API,
        url: `/deployment_template/${id}`,
        headers: authHeaders(),
        action: UPDATE_CUSTOM_TEMPLATE,
        dispatch,
        body: body
      });
      // console.log('updateCustomTemplate result:', result)
      return _.get(result, 'body');
    },
    deleteCustomTemplate: (id, project_id) => async (dispatch, getState) => {
      let result = await del({
        host: Hosts.EDGE_CLOUD_CONTROLLER_API,
        url: `/deployment_template/${id}`,
        params: { project_id },
        headers: authHeaders(),
        action: DELETE_CUSTOM_TEMPLATE,
        dispatch
      });
      // console.log('deleteCustomTemplate result:', result)
      return _.get(result, 'body');
    },
    fetchJupyterNotebooks: (project_id) => async (dispatch, getState) => {
      let result = await get({
        host: Hosts.EDGE_CLOUD_CONTROLLER_API,
        url: `/deployment/list`,
        params: { project_id, template_name: "Jupyter Notebook" },
        headers: authHeaders(),
        action: FETCH_JUPYTER_NOTEBOOKS,
        dispatch,
      });
      return selectAllNotebooks(getState(), project_id);
    },
    fetchDeployments: (project_id) => async (dispatch, getState) => {
      let result = await get({
        host: Hosts.EDGE_CLOUD_CONTROLLER_API,
        url: `/deployment/list?not_template_name=Jupyter%20Notebook&not_template_name=GPU%20Node`,
        params: { project_id },
        headers: authHeaders(),
        action: FETCH_DEPLOYMENTS,
        dispatch,
      });
      return selectAllDeployments(getState(), project_id);
    },
    fetchTemplatesWithFilter: (tags, search_string, page = 1, number = 9) => async (dispatch, getState) => {
      let result = await get({
        host: Hosts.EDGE_CLOUD_CONTROLLER_API,
        url: `/deployment_template/standard_templates`,
        // params: { tags, page: page - 1, number },
        params: {
          tags,
          search: search_string,
          page: page - 1,
          number,
          category: 'serving',
          hidden: false
        },
        headers: authHeaders(),
        action: FETCH_TEMPLATES_WITH_FILTER,
        dispatch,
      });
      return;
    },
    fetchOnDemandTemplatesWithFilter: (tags, search_string, page = 1, number = 9, support_on_demand = null) => async (dispatch, getState) => {
      let result = await get({
        host: Hosts.ONDEMAND_MODEL_API,
        url: `/service/standard_templates`,
        // params: { tags, page: page - 1, number },
        params: { tags, search: search_string, page: page, number },
        headers: authHeaders(),
        action: FETCH_ON_DEMAND_MODELS_WITH_FILTER,
        dispatch,
      });
      return;
    },
    fetchGpuNodes: (project_id) => async (dispatch, getState) => {
      let result = await get({
        host: Hosts.EDGE_CLOUD_CONTROLLER_API,
        url: `/deployment/list`,
        params: { project_id, template_name: "GPU Node" },
        headers: authHeaders(),
        action: FETCH_GPU_NODES,
        dispatch,
      });
      return selectAllGpuNodes(getState(), project_id);
    },
    fetchTemplateTags: () => async (dispatch, getState) => {
      let result = await get({
        host: Hosts.EDGE_CLOUD_CONTROLLER_API,
        url: `/deployment_template/standard_template_tags`,
        action: FETCH_TEMPLATE_TAGS,
        dispatch,
      });
      return;
    },
    fetchOnDemandTags: () => async (dispatch, getState) => {
      let result = await get({
        host: Hosts.EDGE_CLOUD_CONTROLLER_API,
        url: `/deployment_template/standard_template_tags`,
        params: { support_on_demand: true },
        action: FETCH_ON_DEMAND_TAGS,
        dispatch,
      });
      return;
    },
    fetchDeploymentDetail: (project_id, shard, suffix) => async (dispatch, getState) => {
      let result = await get({
        host: Hosts.EDGE_CLOUD_CONTROLLER_API,
        url: `/deployment/${shard}/${suffix}`,
        params: { project_id },
        headers: authHeaders(),
        action: FETCH_DEPLOYMENT_DETAIL,
        dispatch,
      });
      return _.get(result, 'body');
    },
    fetchDeploymentBaseDetail: (project_id, baseId) => async (dispatch, getState) => {
      let result = await get({
        host: Hosts.EDGE_CLOUD_CONTROLLER_API,
        url: `/deployment/base/${baseId}`,
        params: { project_id },
        headers: authHeaders(),
        action: FETCH_DEPLOYMENT_BASE_DETAIL,
        dispatch,
      });
      return _.get(result, 'body');
    },
    fetchDeploymentLogs: (project_id, shard, suffix, isFollow) => async (dispatch, getState) => {
      let result = await get({
        host: Hosts.EDGE_CLOUD_CONTROLLER_API,
        url: `/deployment/${shard}/${suffix}/logs${isFollow ? '?follow=true' : ""}`,
        params: { project_id },
        headers: authHeaders(),
        action: FETCH_DEPLOYMENT_LOGS,
        dispatch,
      });
      return _.get(result, 'body');
    },
    fetchDeploymentEvents: (project_id, shard, suffix) => async (dispatch, getState) => {
      let result = await get({
        host: Hosts.EDGE_CLOUD_CONTROLLER_API,
        url: `/deployment/${shard}/${suffix}/events/v2`,
        params: { project_id },
        headers: authHeaders(),
        action: FETCH_DEPLOYMENT_EVENTS,
        dispatch,
      });
      return _.get(result, 'body');
    },
    deleteDeployment: (project_id, shard, suffix) => async (dispatch, getState) => {
      let result = await del({
        host: Hosts.EDGE_CLOUD_CONTROLLER_API,
        url: `/deployment/${shard}/${suffix}`,
        params: { project_id },
        headers: authHeaders(),
        action: DELETE_DEPLOYMENT,
        dispatch
      });
      // console.log('deleteDeployment result:', result)
      return _.get(result, 'body');
    },
    deleteDeploymentBase: (project_id, baseId) => async (dispatch, getState) => {
      let result = await del({
        host: Hosts.EDGE_CLOUD_CONTROLLER_API,
        url: `/deployment/base/${baseId}`,
        params: { project_id },
        headers: authHeaders(),
        action: DELETE_DEPLOYMENT,
        dispatch
      });
      // console.log('deleteDeployment result:', result)
      return _.get(result, 'body');
    },
    startDeployment: (project_id, id) => async (dispatch, getState) => {
      let result = await put({
        host: Hosts.EDGE_CLOUD_CONTROLLER_API,
        url: `/deployment/base/${id}/start`,
        params: { project_id },
        headers: authHeaders(),
        action: START_DEPLOYMENT,
        dispatch
      });
      return _.get(result, 'body');
    },
    stopDeployment: (project_id, shard, suffix) => async (dispatch, getState) => {
      let result = await put({
        host: Hosts.EDGE_CLOUD_CONTROLLER_API,
        url: `/deployment/${shard}/${suffix}/stop`,
        params: { project_id },
        headers: authHeaders(),
        action: STOP_DEPLOYMENT,
        dispatch
      });
      return _.get(result, 'body');
    },
    fetchRagChatbots: (project_id) => async (dispatch, getState) => {
      let result = await get({
        host: Hosts.EDGE_CLOUD_CONTROLLER_API,
        url: `/chatbot/list`,
        params: { project_id, number: 100 },
        headers: authHeaders(),
        action: FETCH_RAG_CHATBOTS,
        dispatch,
      });
      return result.body;
    },
    fetchRagChatbot: (project_id, id) => async (dispatch, getState) => {
      let result = await get({
        host: Hosts.EDGE_CLOUD_CONTROLLER_API,
        url: `/chatbot/${id}`,
        params: { project_id },
        headers: authHeaders(),
        action: FETCH_RAG_CHATBOT,
        dispatch,
      });
    },
    createRagChatbot: (body) => async (dispatch, getState) => {
      let result = await post({
        host: Hosts.EDGE_CLOUD_CONTROLLER_API,
        url: "/chatbot",
        headers: authHeaders(),
        action: CREATE_RAG_CHATBOT,
        dispatch,
        body: convertToFormData(body)
      });
      return _.get(result, 'body');
    },
    deleteRagChatbot: (project_id, id) => async (dispatch, getState) => {
      let result = await del({
        host: Hosts.EDGE_CLOUD_CONTROLLER_API,
        url: `/chatbot/${id}`,
        params: { project_id },
        headers: authHeaders(),
        action: DELETE_RAG_CHATBOT,
        dispatch
      });
      return _.get(result, 'body');
    },
    updateRagChatbot: (id, body) => async (dispatch, getState) => {
      let result = await put({
        host: Hosts.EDGE_CLOUD_CONTROLLER_API,
        url: `/chatbot/${id}`,
        headers: authHeaders(),
        action: UPDATE_RAG_CHATBOT,
        dispatch,
        data: { project_id: body?.project_id },
        body: body
      });
      return _.get(result, 'body');
    },
    updateRagChatbotName: (project_id, id, name) => async (dispatch, getState) => {
      let result = await put({
        host: Hosts.EDGE_CLOUD_CONTROLLER_API,
        url: `/chatbot/${id}/name`,
        headers: authHeaders(),
        action: UPDATE_RAG_CHATBOT,
        dispatch,
        body: { name, project_id }
      });
    },
    duplicateRagChatbot: (project_id, id) => async (dispatch, getState) => {
      let result = await post({
        host: Hosts.EDGE_CLOUD_CONTROLLER_API,
        url: `/chatbot/${id}/duplicate`,
        headers: authHeaders(),
        action: DUPLICATE_RAG_CHATBOT,
        dispatch,
        params: { project_id },
      });
      return _.get(result, 'body');
    },
    updateSupportSchedule: (chatbot_id, scheduleData) => async (dispatch, getState) => {
      let result = await post({
        host: Hosts.EDGE_CLOUD_CONTROLLER_API,
        url: `/chatbot/${chatbot_id}/support_schedule`,
        headers: authHeaders(),
        dispatch,
        body: scheduleData
      });
      return _.get(result, 'body');
    },
    fetchSupportSchedule: (chatbot_id, project_id) => async (dispatch, getState) => {
      let result = await get({
        host: Hosts.EDGE_CLOUD_CONTROLLER_API,
        url: `/chatbot/${chatbot_id}/support_schedule`,
        params: { project_id },
        headers: authHeaders(),
        dispatch
      });
      return _.get(result, 'body');
    },
    fetchSupportStatus: (chatbot_id, project_id) => async (dispatch, getState) => {
      let result = await get({
        host: Hosts.EDGE_CLOUD_CONTROLLER_API,
        url: `/chatbot/${chatbot_id}/support_status`,
        params: { project_id },
        headers: authHeaders(),
        dispatch
      });
      return _.get(result, 'body');
    },
    fetchLlmEndpints: (pId, cbId) => async (dispatch, getState) => {
      let params = { number: 15 };
      if (cbId) params.chatbot_id = cbId;
      if (pId) params.project_id = pId;

      let result = await get({
        host: Hosts.EDGE_CLOUD_CONTROLLER_API,
        url: `/chatbot/llm_endpoint/list`,
        params,
        headers: authHeaders(),
        action: FETCH_LLM_ENDPOINTS,
        dispatch,
      });
      return _.get(result, 'body.llm_endpoints');
    },
    fetchRegionsFromInstances: (projectId, instanceType) => async (dispatch, getState) => {
      let result = await get({
        host: Hosts.EDGE_CLOUD_CONTROLLER_API,
        url: `/instances`,
        params: { projectId, instanceType, env: (DevEnv === 'staging' ? 'prod' : DevEnv) },
        headers: authHeaders(),
        action: FETCH_REGIONS_FROM_INSTANCES,
        dispatch,
      });

      return result.body;
    },
    fetchRegionsFromCpuInstances: (projectId, instanceType) => async (dispatch, getState) => {
      let result = await get({
        host: Hosts.EDGE_CLOUD_CONTROLLER_API,
        url: `/instances/cpu`,
        params: { projectId, instanceType, env: (DevEnv === 'staging' ? 'prod' : DevEnv) },
        headers: authHeaders(),
        action: FETCH_REGIONS_FROM_INSTANCES,
        dispatch,
      });

      return result.body;
    },
    fetchInstancesFromRegion: (projectId, region) => async (dispatch, getState) => {
      let result = await get({
        host: Hosts.EDGE_CLOUD_CONTROLLER_API,
        url: `/instances`,
        params: { projectId, regions: region, env: (DevEnv === 'staging' ? 'prod' : DevEnv) },
        headers: authHeaders(),
        action: FETCH_INSTANCES_FROM_REGION,
        dispatch,
      });

      return result.body;
    },
    fetchCpuInstancesFromRegion: (projectId, region) => async (dispatch, getState) => {
      let result = await get({
        host: Hosts.EDGE_CLOUD_CONTROLLER_API,
        url: `/instances/cpu`,
        params: { projectId, regions: region, env: (DevEnv === 'staging' ? 'prod' : DevEnv) },
        headers: authHeaders(),
        action: FETCH_INSTANCES_FROM_REGION,
        dispatch,
      });

      return result.body;
    },
    fetchRagAccessToken: (projectId, id) => async (dispatch, getState) => {
      let result = await get({
        host: Hosts.EDGE_CLOUD_CONTROLLER_API,
        url: `/chatbot/${id}/access_token`,
        params: { project_id: projectId },
        headers: authHeaders(),
        action: FETCH_RAG_CHATBOT_ACCESS_TOKEN,
        dispatch,
      });
      return _.get(result, 'body');
    },
    rotateRagAccessToken: (projectId, id) => async (dispatch, getState) => {
      let result = await put({
        host: Hosts.EDGE_CLOUD_CONTROLLER_API,
        url: `/chatbot/${id}/api_key`,
        headers: authHeaders(),
        action: ROTATE_RAG_CHATBOT_ACCESS_TOKEN,
        body: { project_id: projectId },
        dispatch,
      });
      return _.get(result, 'body');
    },
    createLlmEndpoint: (body) => async (dispatch, getState) => {
      let result = await post({
        host: Hosts.EDGE_CLOUD_CONTROLLER_API,
        url: "/chatbot/llm_endpoint",
        headers: authHeaders(),
        action: CREATE_LLM_ENDPOINT,
        dispatch,
        body: body
      });
      return _.get(result, 'body');
    },
    deleteLlmEndpoint: (project_id, chatbot_id, id) => async (dispatch, getState) => {
      let result = await del({
        host: Hosts.EDGE_CLOUD_CONTROLLER_API,
        url: `/chatbot/llm_endpoint/${id}`,
        params: { project_id, chatbot_id },
        headers: authHeaders(),
        action: DELETE_LLM_ENDPOINT,
        dispatch
      });
      return _.get(result, 'body');
    },
    updateLlmEndpoint: (id, body) => async (dispatch, getState) => {
      let result = await put({
        host: Hosts.EDGE_CLOUD_CONTROLLER_API,
        url: `/chatbot/llm_endpoint/${id}`,
        headers: authHeaders(),
        action: UPDATE_LLM_ENDPOINT,
        dispatch,
        body: body
      });
      return _.get(result, 'body');
    },
    fetchChatbotDocuments: (cbId, project_id, page = 0, number = 30) => async (dispatch, getState) => {
      let result = await get({
        host: Hosts.EDGE_CLOUD_CONTROLLER_API,
        url: `/chatbot/${cbId}/document/list?`,
        params: { project_id, page, number },
        headers: authHeaders(),
        action: FETCH_CHATBOT_DOCUMENTS,
        dispatch,
      });
      return _.get(result, 'body');
    },
    fetchChatbotDocument: (cbId, project_id, id) => async (dispatch, getState) => {
      let result = await get({
        host: Hosts.EDGE_CLOUD_CONTROLLER_API,
        url: `/chatbot/${cbId}/document/${id}`,
        params: { project_id },
        headers: authHeaders(),
        action: FETCH_CHATBOT_DOCUMENT,
        dispatch,
      });
      return _.get(result, 'body');
    },
    createChatbotDocument: (id, body) => async (dispatch, getState) => {
      let result = await post({
        host: Hosts.EDGE_CLOUD_CONTROLLER_API,
        url: `/chatbot/${id}/document`,
        headers: authHeaders(),
        action: CREATE_CHATBOT_DOCUMENT,
        dispatch,
        body: convertToFormData(body)
      });
      return _.get(result, 'body');
    },
    deleteChatbotDocument: (project_id, cbId, id) => async (dispatch, getState) => {
      let result = await del({
        host: Hosts.EDGE_CLOUD_CONTROLLER_API,
        url: `/chatbot/${cbId}/document/${id}`,
        params: { project_id },
        headers: authHeaders(),
        action: DELETE_CHATBOT_DOCUMENT,
        dispatch
      });
      return _.get(result, 'body');
    },
    updateChatbotDocument: (chatbotId, id, body, formData = false) => async (dispatch, getState) => {
      let result = await put({
        host: Hosts.EDGE_CLOUD_CONTROLLER_API,
        url: `/chatbot/${chatbotId}/document/${id}`,
        headers: authHeaders(),
        action: UPDATE_CHATBOT_DOCUMENT,
        dispatch,
        body: formData ? body : convertToFormData(body)
      });
      return _.get(result, 'body');
    },
    fetchRagChatbotPrice: () => async (dispatch, getState) => {
      let result = await get({
        host: Hosts.EDGE_CLOUD_API,
        url: `/charge/getChatbotPrice`,
        headers: authHeaders(),
        action: FETCH_RAG_CHATBOT_PRICE,
        dispatch,
      });

      if (!result.body?.[0]?.unit) {
        return null
      }

      const priceObject = _.get(result, 'body').reduce((acc, item) => {
        acc[item.token] = `${formatMoney(parseFloat(item.price) / 100)} per ${item.unit} ${item.token.replace('_', ' ')}`;
        return acc;
      }, {});

      return priceObject;
    },
    fetchOndemandServices: (page = 1, number = 9) => async (dispatch, getState) => {
      let result = await get({
        host: Hosts.ONDEMAND_MODEL_API,
        url: `/service/list?expand=template_id`,
        params: { page: page, number },
        action: FETCH_MODEL_APIS,
        dispatch,
      });
      return selectOndemandModels(getState());
    },
    fetchOndemandServiceById: (id) => async (dispatch, getState) => {
      let result = await get({
        host: Hosts.ONDEMAND_MODEL_API,
        url: `/service/${id}/list?expand=template_id`,
        action: FETCH_MODEL_API_BY_ID,
        dispatch,
      });
      return selectOndemandModels(getState());
    },
    fetchAllQuota: () => async (dispatch, getState) => {
      let result = await get({
        host: Hosts.EDGE_CLOUD_API,
        url: `/quota/list`,
        params: {
        },
        action: FETCH_ALL_QUOTAS,
        dispatch,
      });
      return _.get(result, 'body.quotas');
    },
    fetchChatbotChartData: (cbId, project_id) => async (dispatch, getState) => {
      let result = await get({
        host: Hosts.BQ_STATS_API,
        // url: `/chatbot_stat/chtzum1v9bw6ca20tmhq/analytics`,
        url: `/chatbot_stat/${cbId}/analytics`,
        params: { project_id },
        headers: authHeaders(),
        action: FETCH_CHATBOT_CHART_DATA,
        dispatch,
      });
      return _.get(result, '0');
    },
    fetchChatbotPrompts: (cbId, project_id, body) => async (dispatch, getState) => {
      let result = await post({
        host: Hosts.BQ_STATS_API,
        // host: 'https://bqstats-api.thetaedgecloud.com',
        // url: `/chatbot_stat/chtzum1v9bw6ca20tmhq/prompts_history`,
        url: `/chatbot_stat/${cbId}/prompts_history`,
        params: { project_id },
        headers: authHeaders(),
        action: FETCH_CHATBOT_PROMPTS,
        dispatch,
        body
      });
      return result;
    },
    fetchGpuClusters: (project_id) => async (dispatch, getState) => {
      let result = await get({
        host: Hosts.EDGE_CLOUD_CONTROLLER_API,
        url: `/cluster/list`,
        params: { project_id },
        headers: authHeaders(),
        action: FETCH_GPU_CLUSTERS,
        dispatch,
      });
      return selectAllGpuClusters(getState(), project_id);
    },
    fetchGpuClusterDetail: (project_id, suffix) => async (dispatch, getState) => {
      let result = await get({
        host: Hosts.EDGE_CLOUD_CONTROLLER_API,
        url: `/cluster/${suffix}`,
        params: { project_id },
        headers: authHeaders(),
        action: FETCH_GPU_CLUSTER_DETAIL,
        dispatch,
      });
      return _.get(result, 'body');
    },
    fetchGpuClusterBaseDetail: (project_id, baseId) => async (dispatch, getState) => {
      let result = await get({
        host: Hosts.EDGE_CLOUD_CONTROLLER_API,
        url: `/cluster/base/${baseId}`,
        params: { project_id },
        headers: authHeaders(),
        action: FETCH_GPU_CLUSTER_BASE_DETAIL,
        dispatch,
      });
      return _.get(result, 'body');
    },
    fetchGpuClusterDeployments: (project_id, suffix) => async (dispatch, getState) => {
      let result = await get({
        host: Hosts.EDGE_CLOUD_CONTROLLER_API,
        url: `/cluster/${suffix}/deployments`,
        params: { project_id },
        headers: authHeaders(),
        action: FETCH_GPU_CLUSTER_BASE_DETAIL,
        dispatch,
      });
      return _.get(result, 'body');
    },
    fetchGpuClusterLogs: (project_id, shard, suffix, isFollow) => async (dispatch, getState) => {
      let result = await get({
        host: Hosts.EDGE_CLOUD_CONTROLLER_API,
        url: `/cluster/${shard}/${suffix}/logs${isFollow ? '?follow=true' : ""}`,
        params: { project_id },
        headers: authHeaders(),
        action: FETCH_GPU_CLUSTER_LOGS,
        dispatch,
      });
      return _.get(result, 'body');
    },
    createGpuCluster: (body) => async (dispatch, getState) => {
      let result = await post({
        host: Hosts.EDGE_CLOUD_CONTROLLER_API,
        url: "/cluster",
        headers: authHeaders(),
        action: CREATE_DEPLOYMENT,
        dispatch,
        body: body
      });
    },
    updateGpuCluster: (shard, suffix, body) => async (dispatch, getState) => {
      console.log('updateDeployment body:', body)
      let result = await put({
        host: Hosts.EDGE_CLOUD_CONTROLLER_API,
        url: `/cluster/${shard}/${suffix}`,
        headers: authHeaders(),
        action: UPDATE_DEPLOYMENT,
        dispatch,
        body: body
      });
    },
    deleteGpuCluster: (project_id, suffix) => async (dispatch, getState) => {
      let result = await del({
        host: Hosts.EDGE_CLOUD_CONTROLLER_API,
        url: `/cluster/${suffix}`,
        params: { project_id },
        headers: authHeaders(),
        action: DELETE_DEPLOYMENT,
        dispatch
      });
      return _.get(result, 'body');
    },
    deleteGpuClusterBase: (project_id, baseId) => async (dispatch, getState) => {
      let result = await del({
        host: Hosts.EDGE_CLOUD_CONTROLLER_API,
        url: `/deployment/base/${baseId}`,
        params: { project_id },
        headers: authHeaders(),
        action: DELETE_DEPLOYMENT,
        dispatch
      });
      // console.log('deleteDeployment result:', result)
      return _.get(result, 'body');
    },
    startGpuCluster: (project_id, id) => async (dispatch, getState) => {
      let result = await put({
        host: Hosts.EDGE_CLOUD_CONTROLLER_API,
        url: `/cluster/base/${id}/start`,
        params: { project_id },
        headers: authHeaders(),
        action: START_DEPLOYMENT,
        dispatch
      });
      return _.get(result, 'body');
    },
    stopGpuCluster: (project_id, shard, suffix) => async (dispatch, getState) => {
      let result = await put({
        host: Hosts.EDGE_CLOUD_CONTROLLER_API,
        url: `/cluster/${shard}/${suffix}/stop`,
        params: { project_id },
        headers: authHeaders(),
        action: STOP_DEPLOYMENT,
        dispatch
      });
      return _.get(result, 'body');
    },
    scaleGpuCluster: (project_id, suffix, body) => async (dispatch, getState) => {
      let result = await post({
        host: Hosts.EDGE_CLOUD_CONTROLLER_API,
        url: `/cluster/${suffix}/scale`,
        headers: authHeaders(),
        action: SCALE_GPU_CLUSTER,
        body: { ...body, project_id },
        dispatch
      });
      return _.get(result, 'body');
    },
    setReadEvent: (project_id, shard, suffix) => async (dispatch, getState) => {
      let result = await get({
        host: Hosts.EDGE_CLOUD_CONTROLLER_API,
        url: `/deployment/${shard}/${suffix}/events/read`,
        params: { project_id },
        headers: authHeaders(),
        action: SET_READ_EVENT,
        dispatch,
      });
      return _.get(result, 'body');
    },
  },
  spec: {
    standardTemplates: [],
    totalStandardTemplatesPages: 0,
    prototypingTemplates: [],
    customTemplates: [],
    totalCustomTemplatesPages: 0,
    ondemandModels: { services: [], templates: [] },
    totalOndemandModelPages: 0,
    vms: {},
    neuronVms: {},
    communityVms: {},
    communityVmSpecs: {},
    jupyterNotebooks: {},
    deployments: {},
    gpuNodes: {},
    gpuClusters: {},
    ragChatbots: {},
    llmEndpints: [],
    regionsFromInstances: {},
    instancesFromRegion: {},
    standardTemplateTags: [],
    onDemandTags: [],
    quotas: [],
    deploymentTemplate: {},
  },
  modelReducer: (state, type, body, action) => {
    if (action.url && action.result !== RequestState.SUCCESS)
      return state;

    if (type === FETCH_VMS) {
      const isFullList = action.data?.isFullList ?? false;
      let vms = [];
      let neuronVms = [];
      body.vms.filter(vm => {
        return vm.rank > -1;
      }).forEach(vm => {
        let include = true;

        if (!isFullList && vm.description) {
          try {
            let description = JSON.parse(vm.description);
            const filterLevel = _.get(description, 'hideFilterLevel') || 0;
            include = filterLevel < HideFilterLevel.neuron;
          } catch (e) {
            include = true;
          }
        }

        if (include) {
          vms.push(vm);
        } else {
          neuronVms.push(vm);
        }
      })
      return {
        ...state,
        // vms: _.keyBy(body.vms, 'id'),
        vms: _.keyBy(vms, 'id'),
        neuronVms: _.keyBy(neuronVms, 'id')
      }
    }
    if (type === FETCH_COMMUNITY_VMS) {
      let sortedBody = body.sort((a, b) => {
        if (Number(b.label_nvidia_com_gpu_memory) === Number(a.label_nvidia_com_gpu_memory)) {
          return b.label_nvidia_com_gpu_product.localeCompare(a.label_nvidia_com_gpu_product)
        } else {
          return Number(b.label_nvidia_com_gpu_memory) - Number(a.label_nvidia_com_gpu_memory);
        }
      });
      return {
        ...state,
        communityVms: _.keyBy(sortedBody, 'vm_id')
      }
    }
    if (type === FETCH_COMMUNITY_VM_SPECS) {
      return {
        ...state,
        communityVmSpecs: {
          ...state.communityVmSpecs,
          ..._.keyBy(body, 'NodeName')
        }
      }
    }
    if (type === FETCH_LLM_ENDPOINTS) {
      return {
        ...state,
        llmEndpints: body.llm_endpoints
      }
    }
    if (type === FETCH_CUSTOM_TEMPLATES) {
      const projectId = action.params.project_id;
      return {
        ...state,
        customTemplates: {
          ...state.customTemplates,
          [projectId]: body.templates
        },
        totalCustomTemplatesPages: Math.ceil(Number(body.total_count) / Number(body.number))
      }
    }
    if (type === FETCH_STANDARD_TEMPLATES) {
      return {
        ...state,
        standardTemplates: body.templates,
        totalStandardTemplatesPages: Math.ceil(Number(body.total_count) / Number(body.number))
      }
    }
    if (type === FETCH_TEMPLATES_WITH_FILTER) {
      return {
        ...state,
        standardTemplates: body.templates,
        totalStandardTemplatesPages: Math.ceil(Number(body.total_count) / Number(body.number))
      }
    }
    if (type === FETCH_PROTOTYPING_TEMPLATES) {
      return {
        ...state,
        prototypingTemplates: body.templates,
      }
    }
    if (type === FETCH_TRAINING_TEMPLATES) {
      return {
        ...state,
        trainingTemplates: body.templates,
      }
    }
    if (type === FETCH_DEPLOYMENT_TEMPLATE) {
      return {
        ...state,
        deploymentTemplate: body
      }
    }
    if (type === FETCH_JUPYTER_NOTEBOOKS) {
      const projectId = action.params.project_id;
      return {
        ...state,
        jupyterNotebooks: {
          ...state.jupyterNotebooks,
          [projectId]: _.keyBy(body, 'Suffix')
        }
      }
    }
    if (type === FETCH_DEPLOYMENTS) {
      const projectId = action.params.project_id;
      return {
        ...state,
        deployments: {
          ...state.deployments,
          [projectId]: _.keyBy(body, 'Suffix')
        }
      }
    }
    if (type === FETCH_GPU_NODES) {
      const projectId = action.params.project_id;
      return {
        ...state,
        gpuNodes: {
          ...state.gpuNodes,
          [projectId]: _.keyBy(body.filter((obj) => !(!obj.Suffix && !obj.ID)),
            (obj) => obj.Suffix || obj.ID)
        }
      }
    }
    if (type === FETCH_RAG_CHATBOTS) {
      const projectId = action.params.project_id;
      const existingChatbots = state.ragChatbots[projectId] || {};
      const newChatbots = _.keyBy(body, 'id');

      // Merge existing chatbot properties with new data
      const mergedChatbots = Object.keys(newChatbots).reduce((acc, chatbotId) => {
        acc[chatbotId] = {
          ...existingChatbots[chatbotId],
          ...newChatbots[chatbotId]
        };
        return acc;
      }, {});

      return {
        ...state,
        ragChatbots: {
          ...state.ragChatbots,
          [projectId]: mergedChatbots
        }
      }
    }
    if (type === FETCH_RAG_CHATBOT || type === UPDATE_RAG_CHATBOT) {
      const projectId = action.params?.project_id || action.data?.project_id;
      return {
        ...state,
        ragChatbots: {
          ...state.ragChatbots,
          [projectId]: {
            ...state.ragChatbots[projectId],
            [body['id']]: {
              ...state.ragChatbots?.[projectId]?.[body?.['id']],
              ...body
            }
          }
        }
      }
    }
    if (type === FETCH_DEPLOYMENT_DETAIL) {
      const projectId = action.params.project_id;
      return {
        ...state,
        deployments: {
          ...state.deployments,
          [projectId]: {
            ...state.deployments[projectId],
            [body['Suffix']]: body
          }
        }
      }
    }
    if (type === FETCH_DEPLOYMENT_BASE_DETAIL) {
      const projectId = action.params.project_id;
      return {
        ...state,
        deployments: {
          ...state.deployments,
          [projectId]: {
            ...state.deployments[projectId],
            [body['id']]: body
          }
        }
      }
    }
    if (type === FETCH_REGIONS_FROM_INSTANCES) {
      return {
        ...state,
        regionsFromInstances: body
      }
    }
    if (type === FETCH_INSTANCES_FROM_REGION) {
      // const projectId = action.params.project_id;
      const region = action.params.regions;
      return {
        ...state,
        instancesFromRegion: _.get(body, region)
      }
    }
    if (type === FETCH_MODEL_APIS) {
      let templates = _.get(body, 'templates');
      return {
        ...state,
        ondemandModels: {
          services: _.get(body, 'services'),
          templates: (_.get(body, 'services') || []).map(service => {
            return templates.find(t => t.id === service.template_id);
          })
        },
        totalOndemandModelPages: _.get(body, 'pagination.pages')
      }
    }
    if (type === FETCH_ON_DEMAND_MODELS_WITH_FILTER) {
      let templates = _.get(body, 'templates');
      return {
        ...state,
        ondemandModels: {
          services: _.get(body, 'services'),
          templates: (_.get(body, 'services') || []).map(service => {
            return templates.find(t => t.id === service.template_id);
          })
        },
        totalOndemandModelPages: _.get(body, 'pagination.pages')
      }
    }

    if (type === FETCH_MODEL_API_BY_ID) {
      return {
        ...state,
        ondemandModels: {
          services: state.ondemandModels.services.concat(_.get(body, 'services')),
          templates: state.ondemandModels.templates.concat(_.get(body, 'templates'))
        },
      }
    }

    if (type === FETCH_TEMPLATE_TAGS) {
      return {
        ...state,
        standardTemplateTags: body
      }
    }
    if (type === FETCH_ON_DEMAND_TAGS) {
      return {
        ...state,
        onDemandTags: body
      }
    }

    if (type === FETCH_ALL_QUOTAS) {
      return {
        ...state,
        quotas: _.get(body, 'quotas')
      }
    }

    if (type === FETCH_GPU_CLUSTERS) {
      const projectId = action.params.project_id;
      return {
        ...state,
        gpuClusters: {
          ...state.gpuClusters,
          [projectId]: _.keyBy(body, (obj) => obj.Suffix || obj.ID)
        }
      }
    }

    if (type === FETCH_GPU_CLUSTER_DETAIL) {
      const projectId = action.params.project_id;
      return {
        ...state,
        gpuClusters: {
          ...state.gpuClusters,
          [projectId]: {
            ...state.gpuClusters[projectId],
            [body['Suffix']]: body
          }
        }
      }
    }

    if (type === FETCH_GPU_CLUSTER_BASE_DETAIL) {
      const projectId = action.params.project_id;
      return {
        ...state,
        gpuClusters: {
          ...state.gpuClusters,
          [projectId]: {
            ...state.gpuClusters[projectId],
            [body['id']]: body
          }
        }
      }
    }

    return state;
  }
}
export default Ai;